/**
 * Pure computation of the edit-mode seed for the self-schedule
 * calendar's reducer.
 *
 * Extracted verbatim from `DetailsStep`'s `initialSelfScheduleSlots`
 * memo (pure file separation — no behaviour change). The `useMemo`
 * wrapper + its dependency array stay in `DetailsStep`; only the body
 * moved here so it can be unit-tested.
 */
import type {
  ApiReference,
  AdditionalSlotsContext,
} from '../../../config/types';

type SlotOverrideType =
  | 'none'
  | 'calendar'
  | 'working_hours'
  | 'calendar_working_hours'
  | 'buffer'
  | 'buffer_calendar'
  | 'buffer_working_hours'
  | 'wh_cal_buffer';

export interface InitialSelfScheduleSlot {
  start: string;
  end: string;
  isBooked?: boolean;
  is_custom?: boolean;
  override_type?: SlotOverrideType;
}

interface BuildInitialSelfScheduleSlotsArgs {
  apiReference: ApiReference;
  isSelfSchedule: boolean;
  /** `payloadRef.current.details` — read synchronously by the caller. */
  details: Record<string, unknown>;
  additionalSlotsContext?: AdditionalSlotsContext;
}

export function buildInitialSelfScheduleSlots({
  apiReference,
  isSelfSchedule,
  details,
  additionalSlotsContext,
}: BuildInitialSelfScheduleSlotsArgs): InitialSelfScheduleSlot[] | undefined {
  if (apiReference !== 'edit' || !isSelfSchedule) return undefined;
  const slots = details.selfScheduleSlots as
    | Array<{
        start: string;
        end: string;
        is_custom?: boolean;
        override_type?: SlotOverrideType;
      }>
    | undefined;
  if (!Array.isArray(slots) || slots.length === 0) return undefined;
  const bookedStartMs = additionalSlotsContext
    ? Date.parse(additionalSlotsContext.bookedSlot.start)
    : Number.NaN;
  const bookedEndMs = additionalSlotsContext
    ? Date.parse(additionalSlotsContext.bookedSlot.end)
    : Number.NaN;
  // Forward `is_custom` / `override_type` from the hydrated
  // payload into the seed so the reducer can mark the seeded
  // pick `_fromServer` and the payload mapper preserves the
  // backend's classification on save (unless the user moves it).
  return slots.map((s) => {
    const isBooked =
      Number.isFinite(bookedStartMs) &&
      Number.isFinite(bookedEndMs) &&
      Date.parse(s.start) === bookedStartMs &&
      Date.parse(s.end) === bookedEndMs;
    return {
      start: s.start,
      end: s.end,
      ...(isBooked ? { isBooked: true } : {}),
      ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
      ...(s.override_type ? { override_type: s.override_type } : {}),
    };
  });
}
